import {useMemo} from 'react';
import {assert} from 'assert-ts';
import {selectSettingsPreferredLocale} from 'services/settings';
import {useAppSelector} from 'store/hooks';
import type {CodeList, CodeListId} from '../types';
import {selectCodelistFromCache} from '../codelistSelectors';
import {useRestrictedCodelistsContext} from '../contexts';
import {getDummyCodeList} from './getDummyCodeList';

export const useCodelist = (
  id: CodeListId,
  ignoreRestrictions?: boolean,
): CodeList => {
  const {getRestrictedCodelist, getLocalCodelist} =
    useRestrictedCodelistsContext();
  const language = useAppSelector(selectSettingsPreferredLocale);
  const cached = useAppSelector(state =>
    selectCodelistFromCache(id, language, state),
  );
  return useMemo(() => {
    const codeList = cached ?? getLocalCodelist(id);
    if (assert.soft(codeList, 'useCodeList: Code list not loaded', {id})) {
      // Advanced search allows all values.
      if (ignoreRestrictions) {
        return {
          ...codeList,
          codes: codeList.codes.map(code => ({
            ...code,
            deactivated: false,
            disabled: false,
          })),
        };
      }

      return getRestrictedCodelist(codeList);
    }
    return getDummyCodeList(id);
  }, [cached, getLocalCodelist, getRestrictedCodelist, id, ignoreRestrictions]);
};
